import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Card,
  Col,
  ProgressBar,
  Row,
  Tab,
  Table,
  Tabs,
} from "react-bootstrap";
import PageTitle from "@/components/PageTitle";
import MetricCard from "@/components/analytics/MetricCard";
import EmptyState from "@/components/EmptyState";
import { buildPresetRange } from "@/components/analytics/DateRangeSelector";
import FullScreenLoader from "@/components/loaders/screenLoader";
import {
  AgeBarChartCard,
  GenderPieChartCard,
} from "@/components/analytics/DemographicsCharts";
import { toDisplayLabel } from "@/common/utils/display-labels";
import { fetchDashboardAnalytics } from "@/services/api/analytics.api";
import type {
  AnalyticsFilters,
  DashboardAudience,
  DashboardResponse,
} from "@/types/analytics";

const numberFormatter = new Intl.NumberFormat();
const percentFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const formatNumber = (value: number) => numberFormatter.format(Math.round(value));
const formatPercent = (value: number) => `${percentFormatter.format(value)}%`;

const coreKpiIds = [
  "total-users",
  "new-users",
  "active-users",
  "activation-rate",
];

const audienceCopy: Record<DashboardAudience, string> = {
  all: "All users includes everyone who signed up in the selected range.",
  engaged:
    "Engaged users completed at least one meaningful action in the range: Discovery swipe, event created, wishlist add, or event product added.",
};
const emptyDemographics = { gender: [], ageRanges: [] };

const conversionTone = (rate: number) =>
  rate >= 50 ? "positive" : rate > 0 ? "neutral" : "quiet";

const DashboardPage = () => {
  const [filters] = useState<AnalyticsFilters>(() => ({
    dateRange: buildPresetRange("7d"),
    platform: "all",
  }));
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading"
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const coreKpis = useMemo(() => {
    if (!data) return [];
    return coreKpiIds.flatMap((id) => {
      const match = data.kpis.find((kpi) => kpi.id === id);
      return match ? [match] : [];
    });
  }, [data]);

  useEffect(() => {
    let isActive = true;
    const loadDashboard = async () => {
      setStatus("loading");
      setErrorMessage(null);
      try {
        const response = await fetchDashboardAnalytics(filters);
        if (!isActive) return;
        setData(response);
        setStatus("ready");
      } catch {
        if (!isActive) return;
        setStatus("error");
        setErrorMessage("Unable to load dashboard analytics.");
      }
    };

    void loadDashboard();

    return () => {
      isActive = false;
    };
  }, [filters]);

  const isEmpty = useMemo(() => {
    if (!data) return false;
    const totalValue = coreKpis.reduce((sum, kpi) => sum + kpi.value, 0);
    return totalValue === 0;
  }, [data, coreKpis]);

  const renderError = () => (
    <Alert variant="danger" className="d-flex flex-column gap-3">
      <div>{errorMessage}</div>
    </Alert>
  );

  return (
    <>
      <PageTitle title="Dashboard" />

      {status === "loading" ? <FullScreenLoader /> : null}

      {status === "error" ? renderError() : null}

      {status === "ready" && data && isEmpty ? (
        <Card>
          <EmptyState
            title={
              <>
                A quiet <em>week</em> so far
              </>
            }
            body="No one has signed up or acted in this range yet. The numbers fill in as people arrive."
          />
        </Card>
      ) : null}

      {status === "ready" && data && !isEmpty ? (
        <>
          <Row className="g-3 mb-4">
            {coreKpis.map((kpi) => (
              <Col key={kpi.id} xs={12} sm={6} lg={3}>
                <MetricCard
                  label={toDisplayLabel(kpi.label)}
                  value={
                    kpi.id === "activation-rate"
                      ? formatPercent(kpi.value)
                      : formatNumber(kpi.value)
                  }
                  delta={kpi.changePct}
                  deltaLabel={kpi.comparisonLabel}
                />
              </Col>
            ))}
          </Row>
          <Row className="g-3 mb-4">
            <Col xs={12}>
              <Card>
                <Card.Header className="d-flex align-items-center justify-content-between">
                  <span>Core loop funnel</span>
                  <span className="pz-chip pz-chip--quiet">Conversion view</span>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Step</th>
                        <th className="text-end">Count</th>
                        <th style={{ width: "40%" }}>Conversion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.funnel.map((step, index) => (
                        <tr key={step.id}>
                          <td>
                            <div className="fw-semibold">{toDisplayLabel(step.label)}</div>
                            <div className="text-muted small">
                              {index === 0
                                ? "Starting point"
                                : `From ${toDisplayLabel(data.funnel[index - 1].label)}`}
                            </div>
                          </td>
                          <td className="text-end fw-semibold">
                            {formatNumber(step.count)}
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-3">
                              <span
                                className={`pz-chip pz-chip--${conversionTone(step.conversionRate)}`}
                                style={{ minWidth: 64, justifyContent: "center" }}
                              >
                                {formatPercent(step.conversionRate)}
                              </span>
                              <div className="flex-grow-1">
                                <ProgressBar
                                  now={step.conversionRate}
                                  aria-label={`${toDisplayLabel(step.label)} conversion`}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row className="g-3 mb-4">
            <Col xs={12}>
              <div className="pz-section-head">
                <h2 className="pz-section-head__title">Audience demographics</h2>
                <span className="pz-section-head__meta">
                  Who signs up, and who engages.
                </span>
              </div>
              <Tabs defaultActiveKey="all" className="mb-3">
                {(["all", "engaged"] as DashboardAudience[]).map((audience) => {
                  const demographics =
                    data.demographics?.[audience] ?? emptyDemographics;
                  return (
                    <Tab
                      key={audience}
                      eventKey={audience}
                      title={audience === "all" ? "All users" : "Engaged users"}
                    >
                      <div className="text-muted small mb-3">
                        {audienceCopy[audience]}
                      </div>
                      <Row className="g-3 pb-3">
                        <Col xs={12} lg={5}>
                          <GenderPieChartCard items={demographics.gender} />
                        </Col>
                        <Col xs={12} lg={7}>
                          <AgeBarChartCard items={demographics.ageRanges} />
                        </Col>
                      </Row>
                    </Tab>
                  );
                })}
              </Tabs>
            </Col>
          </Row>
        </>
      ) : null}
    </>
  );
};

export default DashboardPage;
